import events from "@/bl-events"
import { sendUniqEvent } from "@/services/tracking/gtm"
import globals from "@/utils/globals"
import { getCurrentPageUrl } from "@/utils/location"
import { trackEvent } from "@/services/tracking"
import { init as initCookieConsentTracking } from "@/modules/cookie-consent-tracking"
import posthog from "posthog-js"

initCookieConsentTracking()

// This is a global Cookiebot callback which is fired when user 1st time
// accepts consent and on every subsequent page load
// https://support.cookiebot.com/hc/en-us/articles/360003793854-Google-Tag-Manager-deployment

const informAboutCookieConsent = () => {
  if (window.Cookiebot.consent.preferences) {
    sendUniqEvent({ event: "cookieconsent_preferences" })
  }

  if (window.Cookiebot.consent.statistics) {
    sendUniqEvent({ event: "cookieconsent_statistics" })
  }

  if (window.Cookiebot.consent.marketing) {
    sendUniqEvent({ event: "cookieconsent_marketing" })
  }

  // On IE11, occasionally events is undefined here, which interferes with
  // other scripts (like the page-loader and ab test toggler).
  // https://github.com/blinkist/blinkist-web/pull/2276
  if (typeof events !== "undefined") {
    events.emit("cookiebot:accept")
  }
}

const eventHandler = (event) => {
  if (event === "CookiebotOnAccept") {
    informAboutCookieConsent()
  } else if (event === "CookiebotOnDialogDisplay") {
    removeCookiesFromUnclassifiedList()
    removeOldBanner()

    if (globals.autoCookieConsent) {
      removeNewBanner()
      setTimeout(() => {
        window.Cookiebot.submitCustomConsent(false, false, false)
      })
    }
  } else if (event === "CookiebotOnDialogInit") {
    setTimeout(() => {
      globals.store.commit("cookiesSubmitted", false)
    })
  } else if (event === "CookiebotOnLoad") {
    setTimeout(() => {
      globals.store.dispatch("initCookies", {
        consented: window.Cookiebot.consented,
        options: { ...window.Cookiebot.consent }
      })
    })
  }
}

const removeOldBanner = () => {
  const banner = document.getElementById("cookiebanner")
  if (banner) banner.parentNode.removeChild(banner)
}

const removeNewBanner = () => {
  const banner = document.querySelector(".vue-cookie-banner")
  if (banner) banner.parentNode.removeChild(banner)
}

export const hasStatisticsConsent = () => {
  return window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.statistics
}

export const consent = (options) => {
  /**
   * Close the modal immediately after submitting consent instead
   * of waiting for the cookiebot CookiebotOnLoad or CookiebotOnConsentReady callback
   *
   * 2% of users submit consent but the callback is never fired which
   * blocks the user from interacting with the site
   */
  globals.store.dispatch("closeCookieBanner")

  window.Cookiebot.submitCustomConsent(options.preferences, options.statistics, options.marketing)
  trackEvent("store-20200728-user", "1-consent-cookies", options)

  posthog.capture("cookie_consent_submit", {
    preferences: options.preferences,
    statistics: options.statistics,
    marketing: options.marketing,
    consentType: options.consentType
  })

  events.emit("cookies:modified")
}

export const consentAll = (payload) => {
  consent({
    preferences: true,
    statistics: true,
    marketing: true,
    ...payload
  })
}

export const rejectAll = (payload) => {
  consent({
    preferences: false,
    statistics: false,
    marketing: false,
    ...payload
  })
}

const removeUnclassifiedCookie = (cookieName) => {
  if (!window.Cookiebot || !window.Cookiebot.dialog || !window.Cookiebot.dialog.cookieTableUnclassified) {
    return
  }

  const cookieIndex = window.Cookiebot.dialog.cookieTableUnclassified.findIndex((item) => item[0] === cookieName)

  if (cookieIndex < 0) {
    return
  }

  window.Cookiebot.dialog.cookieTableUnclassified.splice(cookieIndex, 1)
}

// HACK: Cookiebot incorrectly adds these cookies under "Unclassified"
// which causes them to be removed when the user accepts the cookie preferences
const removeCookiesFromUnclassifiedList = () => {
  removeUnclassifiedCookie("bk_c_n")
  removeUnclassifiedCookie("bk_onboarding_page_flow")
  removeUnclassifiedCookie("_blinkist-webapp_session")
}

export const initCookiebotEvents = () => {
  // handle all the events that we previously collected before this script was parsed -> see _cookiebot.html.erb
  window.cookiebotEvents && window.cookiebotEvents.forEach(eventHandler)
  // once we made it here, we stop collecting events and switch over to handling events one by one
  window.cookiebotEvents = { push: eventHandler }

  // Allow all cookies on localhost development
  if (globals.env === "development" && getCurrentPageUrl().hostname === "localhost") {
    setTimeout(() => {
      globals.store.dispatch("initCookies", {
        consented: true,
        options: {
          necessary: true,
          preferences: true,
          statistics: true,
          marketing: true
        }
      })
    })
  }
}

initCookiebotEvents()

export default eventHandler
